/* Vuex store, for centralized state management */

let primaryTabs = process.env.VUE_APP_PRIMARY_TABS;
if (primaryTabs) {
  primaryTabs = primaryTabs.split(',');
}

let secondaryTabs = process.env.VUE_APP_SECONDARY_TABS;
if (secondaryTabs) {
  secondaryTabs = secondaryTabs.split(',');
}

import Vue from 'vue';
import Vuex from 'vuex';
import client from 'api-client';
import jobsManager from './jobsManager.js';
import sampManager from './sampManager.js';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    searchForm: {
      fieldsDefault: {},
      fields: {},
      toggleStatus: {},
      fieldErrors: {},
      lastSearchFields: {},
      sortingSequence: [],
      columns: [],
      pageSize: 20,
      currentPage: 1,
      totalResults: 0,
      fileColumnIndex: null,
      query: '',
      advancedSearch: false,
      primaryTab: null,
      secondaryTab: null,
      htmlRows: '',
      searchOnlyInUserGroups: false
    },
    initDone: false,
    loading: true,
    page: 'main',
    showLoginButton: false,
    helpMenuOpened: false,
    userLabel: null,
    groups: null,
    admin: false,
    tapUrl: ''
  },
  modules: {
    filesPanel: jobsManager,
    samp: sampManager
  },
  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
    setToggleStatus(state, payload) {
      Vue.set(state.searchForm.toggleStatus,payload.prefix,payload.booleanValue);
      this.commit('toggleCheckboxes', payload);
    },
    updateGroupsInfo(state, response) {
      state.groups = response.groups;
      state.admin = response.admin;
      if (state.groups !== null && !state.admin) {
        state.searchForm.searchOnlyInUserGroups = true;
      }
    },
    reset(state) {
      Object.keys(state.searchForm.toggleStatus)
        .forEach(toggleKey => { Vue.set(state.searchForm.toggleStatus, toggleKey, null); });
      Object.keys(state.searchForm.fields).forEach(key => {
        state.searchForm.fields[key] = state.searchForm.fieldsDefault[key];
      });
      state.searchForm.advancedSearch = false;
      state.searchForm.secondaryTab = 0;
      state.searchForm.fieldErrors = {};
      this.commit('resetResults');
    },
    resetResults(state) {
      state.searchForm.columns = [];
      state.searchForm.htmlRows = '';
      state.searchForm.sortingSequence = [];
      state.searchForm.totalResults = 0;
      state.searchForm.currentPage = 1;
      state.searchForm.fileColumnIndex = null;
      state.searchForm.query = '';
    },
    toggleCheckboxes(state, payload) {
      Object.keys(state.searchForm.fields)
        .filter(key => key.match('^'+payload.prefix+'_') && key.match('_sel$'))
        .forEach(key => {        
          state.searchForm.fields[key] = payload.booleanValue;
      });
    },
    invalidSearch(state, errors) {
      Object.keys(errors).forEach(key => {
        Vue.set(state.searchForm.fieldErrors, key, errors[key]);
      });
      state.loading = false;
    },
    toggleAdvancedSearch(state) {
      state.searchForm.advancedSearch = !state.searchForm.advancedSearch;
      if (state.searchForm.advancedSearch) {
        state.searchForm.secondaryTab = 0;
      }
      state.searchForm.fieldErrors = {};
    },
    tabChanged(state) {},
    setSearchFilterForTour(state, callback) {
      callback(state);
    },
    setHelpMenuOpened(state, value) {
      state.helpMenuOpened = value;
    }
  },
  actions: {
    init({ commit, dispatch, state }, model) {
      Object.keys(model.fields).forEach(key => {
        Vue.set(state.searchForm.fields, key, model.fields[key]);
        state.searchForm.fieldsDefault[key] = model.fields[key];
      });
      state.showLoginButton = model.showLoginButton;
      state.showLogoutButton = model.showLogoutButton;
      state.userLabel = model.userLabel;
      commit('updateGroupsInfo', model);
      state.tapUrl = model.tapUrl;
      if (!state.tapUrl.endsWith('/')) {
        state.tapUrl += '/';
      }
      state.initDone = true;
      state.loading = false;
      dispatch('checkInvitedRegistrations');
    },
    checkInvitedRegistrations({ commit, state }) {
      client.checkInvitedRegistrations()
        .then(response => {
          if (Object.keys(response).length > 0) {
            commit('updateGroupsInfo', response);
          }
        });
    },
    changePage({ dispatch, state }, page) {
      state.searchForm.currentPage = page;
      dispatch('search', true);
    },
    changePageSize({ dispatch, state }, pageSize) {
      state.searchForm.pageSize = pageSize;
      dispatch('search', true);
    },
    sort({ dispatch, state }, columnKey) {
      let sortingSequence = state.searchForm.sortingSequence;
      if (sortingSequence.length === 1 && sortingSequence[0].columnName === columnKey) {
        // If it is the only sorted column, reverse the order
        sortingSequence[0].sort = sortingSequence[0].sort === 'ASC' ? 'DESC' : 'ASC';
      } else {
        state.searchForm.sortingSequence = [{
          columnName: columnKey,
          sort: 'ASC'
        }];
      }
      dispatch('search', true);
    },
    addOrReverseSort({ dispatch, state }, columnKey) {
      let sortingSequence = state.searchForm.sortingSequence;
      let reversed = false;
      for (var i = 0; i < sortingSequence.length; i++) {
        if (sortingSequence[i].columnName === columnKey) {
          // reverse sort
          sortingSequence[i].sort = sortingSequence[i].sort === 'ASC' ? 'DESC' : 'ASC';
          reversed = true;
          break;
        }
      }
      if (!reversed) {
        // add sort
        state.searchForm.sortingSequence.push({
          columnName: columnKey,
          sort: 'ASC'
        });
      }
      dispatch('search', true);
    },
    search({ commit, state }, keepSorting) {
      state.loading = true;
      if (!keepSorting) {
        state.searchForm.sortingSequence = [];
        state.searchForm.currentPage = 1;
      }
      state.searchForm.fieldErrors = {};
      let fields = {};
      state.searchForm.lastSearchFields = {};
      if (primaryTabs) {
        fields['tab'] = primaryTabs[state.searchForm.primaryTab];
        state.searchForm.fields['tab'] = fields['tab'];
      }
      if (state.searchForm.advancedSearch) {
        fields['secondary_tab'] = secondaryTabs[state.searchForm.secondaryTab];
      }
      Object.keys(state.searchForm.fields).forEach(key => {
        let value = state.searchForm.fields[key];
        if (value !== null && value !== '') {
          fields[key] = value;
        }
      });
      Object.keys(fields).forEach(key => {
        state.searchForm.lastSearchFields[key] = fields[key];
      });
      client.search({
        fields: fields,
        sortingSequence: state.searchForm.sortingSequence,
        page: state.searchForm.currentPage,
        pageSize: state.searchForm.pageSize,
        searchOnlyInUserGroups: state.searchForm.searchOnlyInUserGroups
      }).then(result => {
        state.searchForm.columns = result.columns;
        state.searchForm.htmlRows = result.htmlRows;
        state.searchForm.currentPage = result.page;
        state.searchForm.totalResults = result.totalResults;
        state.searchForm.fileColumnIndex = result.fileColumnIndex;
        state.searchForm.query = result.query;
        state.loading = false;
        commit('samp/updatePageComponents');
      }).catch(response => {
        if (response.status === 400) {
          commit('invalidSearch', response.data);
        }
      });
    }
  }
});
